import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

export type SiteConfig = {
  nombre_comercial: string;
  email_contacto: string;
  telefono_contacto: string;
  whatsapp: string;
  direccion: string;
  ciudad: string;
  codigo_postal: string;
  pais: string;
  hora_checkin: string;
  hora_checkout: string;
  hero_titulo: string;
  hero_subtitulo: string;
  alojamiento_intro: string;
  entorno_intro: string;
  contacto_intro: string;
  tarifas_nota: string;
  politica_cancelacion: string;
  nombre_fiscal: string;
  nif: string;
  registro_mercantil: string;
  numero_registro_turistico: string;
};

export type ServicioPublico = { id: string; nombre: string; icono: string; orden: number };

export type TarifaResumen = {
  baja: number | null;
  media: number | null;
  alta: number | null;
};

export type SitePublicData = {
  config: SiteConfig;
  servicios: ServicioPublico[];
  tarifas: TarifaResumen;
};

const FALLBACK_CONFIG: SiteConfig = {
  nombre_comercial: "El Pajar de Benito",
  email_contacto: "hola@elpajardebenito.es",
  telefono_contacto: "+34 641 696 242",
  whatsapp: "34641696242",
  direccion: "Calle la Poza, 10",
  ciudad: "El Ordial",
  codigo_postal: "19244",
  pais: "España",
  hora_checkin: "16:00",
  hora_checkout: "12:00",
  hero_titulo: "",
  hero_subtitulo: "",
  alojamiento_intro: "",
  entorno_intro: "",
  contacto_intro: "",
  tarifas_nota: "",
  politica_cancelacion: "",
  nombre_fiscal: "CASA JUANA Y LUCIO S.L.",
  nif: "B85745362",
  registro_mercantil: "",
  numero_registro_turistico: "",
};

export const getSitePublicData = createServerFn({ method: "GET" }).handler(
  async (): Promise<SitePublicData> => {
    const url = process.env.SUPABASE_URL;
    const key = process.env.SUPABASE_PUBLISHABLE_KEY;
    if (!url || !key) {
      return { config: FALLBACK_CONFIG, servicios: [], tarifas: { baja: null, media: null, alta: null } };
    }
    const sb = createClient<Database>(url, key, {
      auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    });

    const [cfgRes, svcRes, habRes] = await Promise.all([
      sb
        .from("config_alojamiento")
        .select(
          "nombre_comercial,email_contacto,telefono_contacto,whatsapp,direccion,ciudad,codigo_postal,pais,hora_checkin,hora_checkout,hero_titulo,hero_subtitulo,alojamiento_intro,entorno_intro,contacto_intro,tarifas_nota,politica_cancelacion,nombre_fiscal,nif,registro_mercantil,numero_registro_turistico",
        )
        .eq("id", true)
        .maybeSingle(),
      sb.from("servicios").select("id,nombre,icono,orden").eq("activo", true).order("orden"),
      sb
        .from("habitaciones")
        .select("precio_temporada_baja,precio_temporada_media,precio_temporada_alta")
        .eq("activa", true),
    ]);

    const config: SiteConfig = cfgRes.data
      ? { ...FALLBACK_CONFIG, ...(cfgRes.data as Partial<SiteConfig>) }
      : FALLBACK_CONFIG;

    const servicios = (svcRes.data ?? []) as ServicioPublico[];

    const rows = (habRes.data ?? []) as Array<{
      precio_temporada_baja: number | null;
      precio_temporada_media: number | null;
      precio_temporada_alta: number | null;
    }>;
    const minPositive = (vals: Array<number | null>) => {
      const xs = vals.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0);
      return xs.length ? Math.min(...xs) : null;
    };
    const tarifas: TarifaResumen = {
      baja: minPositive(rows.map((r) => r.precio_temporada_baja)),
      media: minPositive(rows.map((r) => r.precio_temporada_media)),
      alta: minPositive(rows.map((r) => r.precio_temporada_alta)),
    };

    return { config, servicios, tarifas };
  },
);

export const siteConfigQueryOptions = {
  queryKey: ["site-public-data"] as const,
  queryFn: () => getSitePublicData(),
  staleTime: 5 * 60 * 1000,
};